import React, { useState } from 'react'
import { Box, Grid, InputLabel } from '@material-ui/core'
import { FormContext, useForm } from 'react-hook-form'

import { Container, Page } from 'components'
import {
  ConsentReportPdf,
  ExportFormatDialog,
  Header,
  ReportSelection,
  ReportVersionTable,
} from './components'

import getCombinedSchema from './components/ConsentForm/schema'

import * as service from 'service'
import constants from 'constants/index'
import helpers from 'helpers'

import useSnackbar from 'hooks/useSnackbar'
import useStyles from './styles'
import { isEmpty } from 'lodash'

const ConsentReport = () => {
  const [consentForms, setConsentForms] = useState([])
  const [currentReportVersion, setCurrentReportVersion] = useState(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [exportingPdf, setExportingPdf] = useState(false)
  const [loadingExcel, setLoadingExcel] = useState(false)
  const [loadingStats, setLoadingStats] = useState(false)
  const [fields, setFields] = useState([])
  const [refresher, setRefresher] = useState(false)

  const classes = useStyles()
  const snackbar = useSnackbar()

  const formMethods = useForm({
    validationSchema: getCombinedSchema({
      onlyReportVersion: false,
    }),
    defaultValues: {
      reportSelected: '',
      fields: [],
      exportFormat: '',
      legalFrameworkName: '',
      dataAction: constants.reportVersions.DESTROY_VERSION_ID_ACTION,
    },
  })

  const { handleSubmit, triggerValidation, watch } = formMethods

  const dataAction = watch('dataAction')
  const reportSelected = watch('reportSelected')
  const legalBasis = watch('legalFrameworkName')
  const userSelected = watch('email')

  const { consent } = constants.reports.DESCRIPTION
  const { RECEIPT_COMPLETE, RECEIPT_LEGAL_BASIS } =
    constants.reports.TYPES_CONSENTS
  const { EXPORT_FORMAT_TYPES } = constants.reports

  const handleCloseExportFormatDialog = () => setDialogOpen(false)
  const handleRefresh = () => setRefresher((prev) => !prev)

  const getConsentForms = async () => {
    setLoadingStats(true)

    try {
      const response = await service.dponet.consent.getStats({
        legalFrameworkName:
          reportSelected === RECEIPT_LEGAL_BASIS
            ? constants.consentFormReports.LEGAL_FRAMEWORK_NAMES[legalBasis]
            : undefined,
      })

      const consentForms = response?.data?.consentForms || []

      setConsentForms(consentForms)

      if (isEmpty(consentForms)) {
        snackbar.open({
          message: 'Nenhum formulário encontrado!',
          variant: 'error',
        })
      } else {
        setExportingPdf(true)
      }
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error?.response?.data?.error),
        variant: 'error',
      })
    } finally {
      setLoadingStats(false)
    }
  }

  const handleSelectExcelExport = (data) => {
    setLoadingExcel(true)
    try {
      const excelParams = {
        shouldCreateVersion:
          data.dataAction !== constants.reportVersions.DOWNLOAD_ONLY_ID_ACTION,
        userEmail: userSelected?.userEmail,
        ...data,
      }
      if (
        reportSelected === RECEIPT_LEGAL_BASIS &&
        legalBasis === constants.consentFormReports.CONSENT_LEGAL_FRAMEWORK
      ) {
        excelParams.legalFrameworkName =
          constants.consentFormReports.CONSENT_LEGAL_FRAMEWORK_NAME
      } else if (
        reportSelected === RECEIPT_LEGAL_BASIS &&
        legalBasis === constants.consentFormReports.LIA_LEGAL_FRAMEWORK
      ) {
        excelParams.legalFrameworkName =
          constants.consentFormReports.LIA_LEGAL_FRAMEWORK_NAME
      }

      service.dponet.consent.consentFormExportReport(excelParams)

      snackbar.open({
        message: 'O relatório Excel será gerado e enviado por e-mail!',
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(
          error?.response?.data?.error || 'Falha ao exportar relatório Excel.',
        ),
        variant: 'error',
      })
    } finally {
      setLoadingExcel(false)
    }
  }
  const handleSelectPdfExport = async () => {
    if ([RECEIPT_COMPLETE, RECEIPT_LEGAL_BASIS].includes(reportSelected)) {
      return await getConsentForms()
    }

    if (isEmpty(consentForms)) {
      snackbar.open({
        message: 'Nenhum formulário encontrado!',
        variant: 'error',
      })
    } else {
      setExportingPdf(true)
    }
  }

  const onConfirmExport = async (data) => {
    handleCloseExportFormatDialog()
    if (
      data.dataAction === constants.reportVersions.DESTROY_VERSION_ID_ACTION
    ) {
      try {
        await service.dponet.reportVersions.destroy({
          reportVersionId: currentReportVersion?.id,
        })
        setCurrentReportVersion()
      } catch (error) {
        snackbar.open({
          message: helpers.formatters.errorMessage(
            error?.response?.data?.error ||
              'Falha ao remover versão do relatório.',
          ),
          variant: 'error',
        })
        return
      }
    }
    if (data.exportFormat === EXPORT_FORMAT_TYPES.PDF) {
      await handleSelectPdfExport(data)
    } else {
      handleSelectExcelExport(data)
    }

    setTimeout(handleRefresh, 3000)
  }

  const handleOpenExportFormatDialog = async () => {
    const valid = await triggerValidation()
    if (!valid) return
    setDialogOpen(true)
  }

  return (
    <FormContext {...formMethods}>
      <Page className={classes.root} title="Relatório de Consentimentos">
        <Container maxWidth={false} spacingXl={15} spacingLg={10} spacingMd={5}>
          <Box py={5}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Header
                  type="consent"
                  reportTour="consent"
                  isLoading={loadingStats || loadingExcel}
                  buttonAction={handleOpenExportFormatDialog}
                  disabled={!!exportingPdf || loadingExcel}
                  title={consent.title}
                  description={consent.content}
                  buttonType="button"
                />
              </Grid>
              <Grid item xs={12}>
                <InputLabel className={classes.label}>
                  Escolha a versão do relatório:
                </InputLabel>
                <ReportSelection
                  setFields={setFields}
                  consentForms={consentForms}
                  setConsentForms={setConsentForms}
                  setExportingPdf={setExportingPdf}
                  exportingPdf={exportingPdf || loadingExcel}
                />
              </Grid>
              <Grid item xs={12}>
                <ReportVersionTable
                  currentReportVersion={currentReportVersion}
                  kind={constants.reportVersions.KIND.CONSENT}
                  refresher={refresher}
                  setCurrentReportVersion={setCurrentReportVersion}
                />
              </Grid>
            </Grid>
          </Box>
          <ConsentReportPdf
            exportingPdf={exportingPdf}
            setExportingPdf={setExportingPdf}
            consentForms={consentForms}
            shouldCreateVersion={
              dataAction ===
                constants.reportVersions.DESTROY_VERSION_ID_ACTION &&
              !currentReportVersion?.id
            }
            refresh={handleRefresh}
            reportSelected={reportSelected}
            fields={fields}
            userSelected={userSelected}
          />
          <ExportFormatDialog
            open={dialogOpen}
            onClose={handleCloseExportFormatDialog}
            onConfirmExport={handleSubmit(onConfirmExport)}
            isLoadingExcel={loadingExcel}
            currentReportVersion={currentReportVersion}
          />
        </Container>
      </Page>
    </FormContext>
  )
}

export default ConsentReport
